import React, { useContext, useEffect } from 'react';

import { TrackEventContext } from 'common/containers/EventContainer';
import AppcuesLandingHelmet from 'common/helmets/AppcuesLandingHelmet';
import LandingFooterCTA from 'common/LandingFooterCTA';
import LazyLoadedImage from 'common/LazyLoadedImage';
import UppercaseHeader from 'common/UppercaseHeader';
import LandingCaseStudyHeadingText from 'common/www/LandingCaseStudyHeadingText';

import AppcuesWordmark from 'img/landing/appcues-wordmark.svg';

import 'css/components/www/_LandingCustomersPage.scss';

const AppcuesLanding = () => {
  const trackEvent = useContext(TrackEventContext);

  useEffect(() => {
    trackEvent('Viewed Appcues Case Study');
  }, [trackEvent]);

  return (
    <div className="landingAppcues landingCustomersPage">
      <AppcuesLandingHelmet />
      <div className="fullWidthContainer">
        <div className="header">
          <LandingCaseStudyHeadingText title="How Appcues closes the feedback loop with Canny" />
          <div className="headerVideo">
            <iframe
              width="560"
              height="315"
              src="https://www.youtube.com/embed/a4Ja7cIyp9U"
              title="YouTube video player"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen></iframe>
          </div>
        </div>
      </div>
      <div className="mainContent">
        <div className="mainContentText">
          <div className="left">
            <p>Appcues is an onboarding experience tool. Its main goals are to:</p>
            <ol>
              <li>Improve end-user onboarding</li>
              <li>Measure the impact of your onboarding efforts</li>
            </ol>
            <p>Appcues lets you add all this to your onboarding flow (no code needed!): </p>
            <ul>
              <li>Guided tours</li>
              <li>Checklists</li>
              <li>Tooltips</li>
              <li>Customized and segmented messages</li>
            </ul>
            <p>This helps teams expose users to their app's most important features faster.</p>
            <p>
              We talked to Joey Muething and Lyla Rozelle from AppCues&apos; product team. They both
              experienced Appcues as users first.
            </p>
            <p>
              Joey joined Appcues as a product manager 2.5 years ago and is currently focused on
              integrations and analytics. Lyla first joined the marketing team and moved into a
              product-focused role later.
            </p>

            <h2 className="sectionTitle">Problem</h2>
            <p>Before Canny, Appcues was already getting lots of feedback.</p>
            <p className="quote">
              "We had a massive fire hose of feedback that went into Slack. Then, it ended up in the
              world&apos;s most annoying Airtable &mdash; unorganized with no way of prioritizing
              it." - Lyla
            </p>
            <p>
              Overall, Appcues lacked the greater context of their feedback. They could manually
              quantify how many requests each feature got but couldn&apos;t go beyond that.
            </p>
            <p className="quote">
              "Our sales and support teams were doing their best when passing on feedback. But we
              still ended up playing telephone. Inevitably, the true nature of the ask got lost, and
              we had to go back to the customer and clarify. This was a frustrating experience for
              both our team and our customers."
            </p>
            <p>
              The product team realized their customers were indirectly asking for something like
              Canny.
            </p>
            <p className="quote">
              "Our users started asking: 'What are other customers saying about this? Are they
              requesting this too? Or are we asking for too much?' When we read between the lines of
              these questions, we realized that they wanted a community where they could discuss
              these things and see what others were saying."
            </p>

            <p>
              Appcues initially contemplated building a feedback portal in-house. However, as they
              outlined their needs, they realized they needed more than just a feedback tool. They
              realized that they also needed to:
            </p>
            <ul>
              <li>Prioritize and segment feedback</li>
              <li>Show progress to their customers</li>
              <li>Close the feedback loop</li>
              <li>Separate feedback between internal and public-facing boards</li>
            </ul>

            <p>
              One of their biggest time-consuming activities was looking for potential beta testers.
            </p>
            <p className="quote">
              “Simply finding customers who asked for a certain feature or users who asked for
              something similar was a huge time suck.”
            </p>

            <h2 className="sectionTitle">Solution</h2>
            <p className="bold">Onboarding Canny</p>
            <p>
              When Appcues found Canny, it clicked almost instantly. Their own software&apos;s
              installation process is almost the same as Canny&apos;s.
            </p>
            <p className="quote">
              "It&apos;s very straightforward and quick. Our engineers looked at it and instantly
              knew what to do. The embed was also very easy."
            </p>
            <p className="bold">Tracking Feedback</p>
            <p>
              Now Appcues&apos; users have a central place to leave feedback and engage with others.
            </p>
            <p className="quote">
              "Our customers are putting their fire-hose mentality into Canny with upvotes and
              regular feedback. A lot of times, customers write their requests themselves. If they
              prefer us to write it on their behalf, we have that option too. We can always include
              a snippet from Gong, for example, to make sure that we&apos;re passing on the feedback
              exactly as it came."
            </p>

            <p>The product team isn&apos;t the only department that benefits from Canny.</p>

            <p className="quote">
              “Lots of feedback comes from the CX or the sales team. Sales tells us &mdash; we lost
              this deal because feature X is missing. But now they can create a new post in Canny or
              add an internal comment about this on an existing post. Alternatively, they can ask
              the prospect to upvote or comment on this post and get notified when it&apos;s built.
              This gives every customer a feeling that they&apos;re heard and valued.”
            </p>

            <p className="bold">Integrations & more</p>
            <p>
              Appcues didn&apos;t stop at just setting up a feedback portal. They added the
              following to get the most out of Canny:
            </p>

            <ul>
              <li>
                Canny&apos;s Slack integration to get notifications about every action on their
                boards
              </li>
              <li>A “submit a feature request” link in their main navigation</li>
              <li>
                A link to the same request form throughout their app (with pre-filled forms to make
                it easier for users)
              </li>
              <li>Our Salesforce integration to help their sales team track feedback better</li>
            </ul>

            <p>
              The Salesforce integration plays a big role in how Appcues prioritizes feedback. Their
              team sorts feature requests by MRR (that comes from Salesforce). And they push votes
              and comments from Canny to Salesforce&apos;s notes section on each account. This helps
              the sales and CX teams give customers updates and just know more about every customer.
            </p>

            <p className="bold">Managing data</p>
            <p>
              There was one thing Appcues didn&apos;t expect to find and use in Canny &mdash; the
              merge feature. The product team can easily identify duplicated requests and merge them
              with one click. It saves time and helps the team keep their boards clean and
              organized.
            </p>

            <p className="bold">Finding beta testers</p>

            <p>
              Canny helps Appcues in one more unexpected way. Appcues is big on beta-testing
              features. And who&apos;s a better potential tester than a user who requested that
              particular feature? Now, finding them is quick and seamless.
            </p>

            <p className="quote">
              “Finding beta testers is so much easier now. I just go to a specific Canny post,
              export the list of voters, and email them. This is much better than trying to go
              through all notes in each Salesforce account for example.”
            </p>

            <p className="bold">Closing the feedback loop</p>
            <p>
              Overall, both Lyla and Joey shared that their favorite thing about Canny is closing
              the feedback loop. Taking in feedback, keeping users updated, and notifying them about
              the changes helps them feel accomplished.
            </p>

            <h2 className="sectionTitle">Results</h2>
            <p>
              Appcues&apos; team feels much more at ease thanks to Canny. They&apos;re not worried
              about missing feedback, disappointing customers, or choosing the wrong features to
              build.
            </p>
            <p className="quote">
              “It&apos;s easy to forget how bad the feedback loop could be sometimes in many SaaS
              companies. That&apos;s evident from the customer reaction we get. When a user submits
              feedback and we respond almost right away, they&apos;re really taken aback. “Wait,
              someone actually gets and reads this? I was expecting it to go into a black hole and
              never hear back,” &mdash; is a typical client response.”
            </p>

            <p>
              Giving clients the feeling that they matter makes all the difference. It positively
              affects CSAT and NPS scores for Appcues and is often the deciding factor in
              customers&apos; renewal decisions. Appcues customers often say: “The reason I&apos;m
              sticking around is because you listen to my feedback.”
            </p>

            <p>
              The team also keeps track of their top 20 feature requests (prioritized by MRR and
              number of votes). They call it their “temperature check.” They&apos;ve noticed
              significant time savings and less confusion overall.
            </p>

            <p className="quote">
              “Nobody is asking where they can leave feedback anymore. No one is wondering &mdash;
              has anyone else asked for this? What&apos;s the status of that? We&apos;re a remote
              company, so transparency is crucial. Since implementing Canny, things are so much more
              streamlined and all in one central place.”
            </p>

            <p>
              The team has also increased the amount of feedback they&apos;re getting thanks to
              Canny.
            </p>

            <p className="quote">
              “I got to attribute that to repeat customers. Before Canny, maybe they submitted
              feedback, never heard back, and never bothered to share their thoughts again.
              It&apos;s different now. There&apos;s very low friction &mdash; it&apos;s easy to
              communicate and share what&apos;s on their mind.”
            </p>

            <p>
              We&apos;re happy to help Appcues connect with their customers better and save time.{' '}
              <a href="https://canny.io/register" rel="noopener" target="_blank">
                Get a free Canny account today
              </a>
              , and you can do the same.
            </p>
          </div>
          <div className="right">
            <LazyLoadedImage
              alt="Appcues Company"
              className="companyLogo appCuesWordmark"
              src={AppcuesWordmark}
            />
            Appcues makes it easy to measure and improve product adoption without a developer. The
            no-code platform empowers non-technical teams to track and analyze product usage. It
            helps you publish beautiful in-app onboarding tours, announcements, and surveys, in
            minutes.
            <a href="https://www.appcues.com/" rel="noopener" target="_blank">
              appcues.com
            </a>
            <div className="stat">
              <UppercaseHeader>Employees</UppercaseHeader>
              <p>90</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Founded</UppercaseHeader>
              <p>2015</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Years on Canny</UppercaseHeader>
              <p>1</p>
            </div>
            <div className="stat">
              <UppercaseHeader>Votes on Canny</UppercaseHeader>
              <p>1,500+</p>
            </div>
          </div>
        </div>
        <LandingFooterCTA style="solid" expanded={true} />
      </div>
    </div>
  );
};

export default AppcuesLanding;
